// Global config for FLOW

import type { MenuItem } from '@neo/blocks/context-menu'
import { produceWithAutoFreezeDisabled } from '@shared/utils'
import { MarkerType, type Node } from '@xyflow/react'
import { cloneDeep, merge } from 'lodash'
import { ArrowRight, ClipboardCopy, ClipboardPaste, Copy, Pencil, Trash2 } from 'lucide-react'
import { v4 } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { FlowTypes } from 'v2/types'
import type { z } from 'zod'
import { errorToast, successToast } from '../../../utils/toasts'
import { CustomEdge } from '../components/edge-with-delete'
import { useFlowStore } from '../store'
import {
  AgentState,
  type AiAgentNodeformData,
  FlowNodeTypes,
  type GenericNode,
  type NodeDataBase,
  type RFlowPayload,
  type RFlowsPaginated,
} from '../types'
import { getDefaultNodeData } from '../utils'
import { subAccountLockedNodeReason } from '../utils/sub-account-locks'
import type { agentSettingsSchema } from './agent-settings.validation.config'
import { v2validations } from './validation.config'

export const defaultEdge = {
  type: 'button',
  style: { strokeDasharray: 4, strokeWidth: 2 },
  markerEnd: {
    // 9, not React Flow's default 12.5. markerUnits is 'strokeWidth', so the
    // default multiplied by this edge's 2px stroke into a ~25px solid triangle
    // — the heaviest mark on the canvas, against nodes drawn in hairlines.
    // No `color` here on purpose: React Flow builds the marker's DOM id by
    // concatenating its props, so a token value (`hsl(var(--tertiary))`) would
    // put parentheses in an id that is then referenced as url(#id) — the first
    // `)` closes the url() and the arrow disappears. The colour is applied from
    // style.scss instead.
    type: MarkerType.ArrowClosed,
    width: 9,
    height: 9,
  },
}

export const DEFAULT_NOTE_Z_INDEX = -1000

export const edgeTypes = {
  button: CustomEdge,
}

export const defaultRFSelectTriggerNode = (x: number, y: number) => ({
  id: v4(),
  type: FlowNodeTypes.select_trigger,
  name: 'Select Trigger',
  position: { x, y },
  data: {
    meta: { name: 'Select Trigger' },
    config: {
      name: 'Select Trigger',
    },
  },
})

export const defaultSelectTriggerNode = {
  id: v4(),
  type: FlowNodeTypes.select_trigger,
  top: 50,
  left: typeof window !== 'undefined' ? Math.floor((window.innerWidth - 250) / 2) : 550,
  name: 'Select Trigger',
  component: FlowNodeTypes.select_trigger,
  config: {
    model: [{ data: { name: 'Select Trigger' }, type: 'string' }],
  },
  changed: false,
}

// In a dedicated service or hook
export const getFlowsCountFromCache = (): number => {
  const queryKey = [...flowQueryKeys().paginatedFlows, { pageIndex: 0, pageSize: 50 }, []]
  return queryClient.getQueryData<RFlowsPaginated>(queryKey)?.total || 0
}

// A new agent always starts on a blank canvas — the agent-type picker is gone and Vibe
// Agent builds the flow from here.
export const getNewFlowData = (type?: FlowTypes): RFlowPayload => {
  const flowsCount = getFlowsCountFromCache()

  return {
    nodes: [defaultSelectTriggerNode],
    connections: [],
    phlo: {
      name: `Agent Flow ${flowsCount ? flowsCount + 1 : 1}`,
      group: 'public',
      enabled: false,
      description: 'Flow description',
      changed: true,
      redact: false,
      state: AgentState.DRAFT,
      phlo_type: type,
      // the first save stamps a new flow v2.10, so the builder runs it as one from the start
      attributes: { agent_engine_version: 'v2.10' },
    },
    notes: {
      notes: [],
    },
    global_meta: null,
  }
}

// This context menu is shared between keyboard shortcuts and right click
export const getContextMenu = (
  contextOpenNode: Node<NodeDataBase<unknown>>,
  // This is needed so that we dont execute the shortcut if node is not hovered
  hoveredNodeRef: React.MutableRefObject<Node<NodeDataBase<unknown>> | null>,
  isEditing: boolean,
  event?: Event,
) => {
  event?.stopPropagation()
  const { flowInstance, selectNode } = useFlowStore.getState()

  const execute = (callback: (node: GenericNode, nodes?: GenericNode[]) => void) => () => {
    const { selectedNodeID } = useFlowStore.getState()
    const nodes = flowInstance?.getNodes()
    const isContextMenuOpen = window.document.querySelector('[data-state="open"]')
    const selectedNode = nodes?.find((node) => node.id === selectedNodeID)
    const nodeToExecute = contextOpenNode || selectedNode
    if (selectedNode || hoveredNodeRef.current || isContextMenuOpen) {
      callback(nodeToExecute, nodes)
      // This is done to close the context menu
      window.document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    }
  }

  const legacyAiAgentNodeTypes = [
    FlowNodeTypes.ai_agent_chat,
    FlowNodeTypes.ai_agent_call,
    FlowNodeTypes.ai_agent_message,
    FlowNodeTypes.ai_agent_whatsapp,
  ]
  const newAiAgentNodeTypes = [
    FlowNodeTypes.ai_agent_v2,
    FlowNodeTypes.ai_agent_v29,
    FlowNodeTypes.ai_action,
  ]
  // A locked node is read-only, and a copy would be a palette add by another route.
  const isLockedNode = !!subAccountLockedNodeReason(contextOpenNode?.type)
  const items: MenuItem<Node<NodeDataBase<unknown>>>[] = [
    {
      type: 'item',
      label: 'Delete',
      icon: <Trash2 size={16} />,
      onSelect: execute((node) => {
        flowInstance?.deleteElements({ nodes: [node] })
        if (node.type === FlowNodeTypes.start) {
          flowInstance?.addNodes([defaultRFSelectTriggerNode(node.position.x, node.position.y)])
        }
      }),
      shortcut: {
        key: 'backspace',
      },
      visible: isEditing,
    },
    {
      type: 'item',
      label: 'Change Start Trigger',
      icon: <ArrowRight size={16} />,
      onlyOnHover: true,
      visible: contextOpenNode.type === FlowNodeTypes.start && isEditing,
      onSelect: execute(() => {
        // Show trigger dialog
        useFlowStore.getState().setShowTriggerDialog(true)
      }),
    },
    {
      type: 'item',
      label: 'Rename',
      icon: <Pencil size={16} />,
      onlyOnHover: true,
      visible: contextOpenNode?.type !== FlowNodeTypes.start && isEditing && !isLockedNode,
      onSelect: execute((node) => {
        selectNode(node.id)
        useFlowStore.getState().requestNameFieldFocus()
      }),
    },
    {
      type: 'item',
      label: 'Duplicate',
      icon: <Copy size={16} />,
      onlyOnHover: true,
      visible: contextOpenNode?.type !== FlowNodeTypes.start && isEditing && !isLockedNode,
      shortcut: {
        key: 'd',
      },
      onSelect: execute((node, nodes) => {
        const clonedNode = cloneDeep(node)
        const { name } = getDefaultNodeData(
          clonedNode.type as FlowNodeTypes,
          () => nodes ?? [],
        ).config
        const newNode = {
          data: {
            ...clonedNode.data,
            // The clone starts AI-named again: its name is regenerated below, so a
            // pin copied from the source node would freeze the wrong name.
            name_source: undefined,
            config: {
              ...clonedNode.data.config,
              name,
            },
          },
          type: clonedNode.type,
          id: v4(),
          position: { x: node.position.x + 150, y: node.position.y + 150 },
        }
        flowInstance?.addNodes([newNode])
        // Select the new node after it is added to the graph
        setTimeout(() => {
          selectNode(newNode.id)
        }, 500)
      }),
    },
    {
      type: 'item',
      label: 'Copy AI Configuration',
      icon: <ClipboardCopy size={16} />,
      onSelect: execute((node) => {
        if (navigator.clipboard && node.data.config) {
          navigator.clipboard
            .writeText(JSON.stringify({ sourceNodeId: node.id, config: node.data.config }, null, 2))
            .then(() => {
              successToast({
                title: 'Configuration Copied',
                description: 'AI configuration has been copied to clipboard',
              })
            })
            .catch(() => {
              errorToast({
                title: 'Copy Failed',
                description: 'Failed to copy configuration to clipboard. Please try again.',
              })
            })
        } else if (!navigator.clipboard) {
          errorToast({
            title: 'Clipboard Access Denied',
            description: 'Unable to access clipboard. Please check your browser permissions.',
          })
        }
      }),
      visible:
        (contextOpenNode &&
          legacyAiAgentNodeTypes.includes(contextOpenNode.type as FlowNodeTypes) &&
          (contextOpenNode.data.config as AiAgentNodeformData)?.conversation_type ===
            'ai_generated') ||
        newAiAgentNodeTypes.includes(contextOpenNode.type as FlowNodeTypes),
    },
    {
      type: 'item',
      label: 'Paste AI Configuration',
      icon: <ClipboardPaste size={16} />,
      visible:
        isEditing &&
        contextOpenNode &&
        (legacyAiAgentNodeTypes.includes(contextOpenNode.type as FlowNodeTypes) ||
          newAiAgentNodeTypes.includes(contextOpenNode.type as FlowNodeTypes)),
      onSelect: execute(async (nodeToUpdate) => {
        const targetNodeType = nodeToUpdate.type as FlowNodeTypes

        if (!navigator.clipboard || !navigator.clipboard.readText) {
          errorToast({
            title: 'Clipboard Access Denied',
            description: 'Unable to access clipboard. Please check your browser permissions.',
          })
          return
        }

        const specificAgentType = targetNodeType as
          | FlowNodeTypes.ai_agent_chat
          | FlowNodeTypes.ai_agent_call
          | FlowNodeTypes.ai_agent_message
          | FlowNodeTypes.ai_agent_whatsapp

        const allValidations = v2validations()
        const targetSchema = allValidations[specificAgentType]

        if (!targetSchema) {
          return
        }

        try {
          const clipboardText = await navigator.clipboard.readText()
          if (!clipboardText) {
            errorToast({
              title: 'Empty Clipboard',
              description: 'No configuration found in clipboard.',
            })
            return
          }

          let parsedClipboardConfig: { config: AiAgentNodeformData; sourceNodeId: string }
          try {
            parsedClipboardConfig = JSON.parse(clipboardText)
          } catch (_jsonError) {
            errorToast({
              title: 'Invalid Configuration Data',
              description: 'The clipboard content is invalid and cannot be parsed.',
            })
            return
          }

          if (
            typeof parsedClipboardConfig !== 'object' ||
            parsedClipboardConfig === null ||
            !parsedClipboardConfig.config || // Check for the nested config object
            typeof parsedClipboardConfig.config !== 'object'
          ) {
            errorToast({
              title: 'Invalid Configuration',
              description: 'The clipboard content is not a valid configuration.',
            })
            return
          }

          const { config: configFromClipboard, sourceNodeId } = parsedClipboardConfig

          const configToApply: Partial<AiAgentNodeformData> = {}
          let hasPastedAnything = false

          const fieldsToIgnore = ['name', 'id']

          if (legacyAiAgentNodeTypes.includes(targetNodeType)) {
            fieldsToIgnore.push('channel')
          }
          if (
            targetNodeType !== FlowNodeTypes.ai_agent_call &&
            legacyAiAgentNodeTypes.includes(targetNodeType)
          ) {
            fieldsToIgnore.push('model_config')
          }

          Object.keys(targetSchema.shape).forEach((key) => {
            if (fieldsToIgnore.includes(key)) return

            if (Object.hasOwn(configFromClipboard, key)) {
              const valueToValidate = configFromClipboard[key as keyof AiAgentNodeformData]
              // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
              const fieldSchema = (targetSchema.shape as any)[key as keyof AiAgentNodeformData]

              if (fieldSchema && typeof fieldSchema.safeParse === 'function') {
                const fieldValidationResult = fieldSchema.safeParse(valueToValidate)
                if (fieldValidationResult.success) {
                  configToApply[key as keyof AiAgentNodeformData] = fieldValidationResult.data
                  hasPastedAnything = true
                }
              }
            }
          })

          if (hasPastedAnything) {
            flowInstance?.setNodes((currentNodes) =>
              currentNodes.map((n) => {
                if (n.id === nodeToUpdate.id) {
                  const existingConfig = n.data.config || {}
                  const finalPastedConfig = merge({}, existingConfig, configToApply)
                  // HACK: Force sidebar re-render by briefly changing and then restoring the selected node ID.
                  // The sidebar re-renders when the selected nodeId changes. By selecting the sourceNodeId (which has the new config)
                  // and then immediately back to the current node's ID, we trigger this re-render.
                  if (sourceNodeId) {
                    selectNode(sourceNodeId)
                  }
                  queueMicrotask(() => {
                    selectNode(n.id)
                  })
                  // n is of type Node<NodeDataBase<unknown>> based on flowInstance.getNodes()
                  // Explicitly type draftNode to help linter.
                  return produceWithAutoFreezeDisabled(
                    n,
                    (draftNode: Node<NodeDataBase<unknown>>) => {
                      draftNode.data.config = finalPastedConfig
                      return undefined
                    },
                  )
                }
                return n
              }),
            )
            successToast({
              title: 'Configuration Pasted',
              description: 'AI configuration has been successfully applied to the node',
            })
          } else {
            errorToast({
              title: 'No Compatible Fields',
              description:
                'No compatible fields were found in the clipboard to paste for this node type.',
            })
          }
        } catch (_error) {
          errorToast({
            title: 'Paste Failed',
            description:
              'Failed to paste configuration. Please ensure you have copied a valid configuration.',
          })
        }
      }),
    },
  ]

  return items.filter((item) => item.visible !== false)
}

export const FLOW_CLASSNAMES = {
  node: 'react-flow__node',
  nodeInner: 'node-inner',
  nodeError: ['border-status-danger', 'hover:border-status-danger'],
}

export type AgentSettingsFormValues = z.infer<typeof agentSettingsSchema>
